const VAT_EXEMPT_SUFFIX = "-VAT-EXEMPT";
const SessionStorageKey = Object.freeze({
    VAT_NAME: "vatExemptionName",
    VAT_REASON: "vatExemptionReason",
    VAT_CHECKBOX: "vatExemptionCheckbox"
});

// ---------- Cart & VAT logic ----------
async function cartContainsVatFree() {
    const data = await sendRequest("cart").then(r => r.json());
    return data.items.some(item => item.sku && item.sku.endsWith(VAT_EXEMPT_SUFFIX));
}

async function swapVat(includeVat) {
    const cartData = await sendRequest("cart").then(r => r.json());

    // Prepare the list of items to swap
    const swapPromises = cartData.items.map(async ({ url, quantity, sku, id }) => {
        if (!sku || typeof sku !== 'string') return null; // Ignore virtual products like gift cards
        let vatSku, variantResponse;

        if (includeVat) {
            if (!sku.endsWith(VAT_EXEMPT_SUFFIX)) return null; // Skip non-VAT items
            vatSku = sku.slice(0, -VAT_EXEMPT_SUFFIX.length);
            variantResponse = await fetch(url.split("?")[0].replace(/-vat-exempt/i, "") + ".js");
        } else {
            if (sku.toUpperCase().endsWith(VAT_EXEMPT_SUFFIX.toUpperCase())) return null; // Skip VAT items
            vatSku = sku + VAT_EXEMPT_SUFFIX;
            variantResponse = await fetch(url.split("?")[0] + "-vat-exempt.js");
        }

        if (!variantResponse.ok) {
            console.warn(`Failed to fetch variant: ${vatSku}`, url);
            return null;
        }
        
        const variantData = await variantResponse.json();
        const variant = variantData.variants.find(v => v.sku === vatSku);
        if (!variant) return null;

        return includeVat
            ? { id: variant.id, quantity, vatFreeId: id }
            : { id, quantity, vatFreeId: variant.id };
    });

    const results = (await Promise.all(swapPromises)).filter(Boolean);
    if (results.length === 0) return;

    // Prepare updates
    const productsToAdd = Object.fromEntries(
        includeVat
            ? results.map(({ id, quantity }) => [id, quantity])
            : results.map(({ vatFreeId: id, quantity }) => [id, quantity])
    );

    const productsToRemove = Object.fromEntries(
        includeVat
            ? results.map(({ vatFreeId: id }) => [id, 0])
            : results.map(({ id }) => [id, 0])
    );

    await updateProducts({ ...productsToRemove, ...productsToAdd });
}

// ---------- Session Storage helpers ----------
function saveVatExemptionName() {
    const val = document.getElementById("vat_exemption_name")?.value || "";
    sessionStorage.setItem(SessionStorageKey.VAT_NAME, val);
}

function saveVatExemptionReason() {
    const val = document.getElementById("vat_exemption_reason")?.value || "";
    sessionStorage.setItem(SessionStorageKey.VAT_REASON, val);
}

function saveVatExemptionCheckbox() {
    const val = !!document.getElementById("vat_exemption_checkbox")?.checked;
    sessionStorage.setItem(SessionStorageKey.VAT_CHECKBOX, val);
}

function fillVatExemptionName(targetInputId) {
    const val = sessionStorage.getItem(SessionStorageKey.VAT_NAME);
    if (val !== null && document.getElementById(targetInputId)) {
        document.getElementById(targetInputId).value = val;
    }
}

function fillVatExemptionReason(targetInputId) {
    const val = sessionStorage.getItem(SessionStorageKey.VAT_REASON);
    if (val !== null && document.getElementById(targetInputId)) {
        document.getElementById(targetInputId).value = val;
    }
}

function fillVatExemptionCheckbox(targetInputId) {
    const val = sessionStorage.getItem(SessionStorageKey.VAT_CHECKBOX);
    if (val !== null && document.getElementById(targetInputId)) {
        document.getElementById(targetInputId).checked = val === "true";
    }
}

// ---------- Shopify request helpers ----------
async function sendRequest(endpoint, method = "GET", body = null, request = {}) {
    const response = await fetch(`${window.Shopify.routes.root}${endpoint}.js`, {
        ...request,
        method,
        ...(body ? { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } } : {})
    });

    if (response.status >= 500) throw new Error(response.statusText);
    if (response.status >= 400) {
        const json = await response.json();
        throw new Error(`${json.message}: ${json.description}`);
    }
    
    return response;
}

async function addProducts(products) {
    await sendRequest("cart/add", "POST", { items: products });
}

async function updateProducts(quantities) {
    await sendRequest("cart/update", "POST", { updates: quantities });
}

// ---------- Initialize VAT form & checkout ----------
document.addEventListener("DOMContentLoaded", () => {
    const checkbox = document.getElementById("vat_exemption_checkbox");
    const vatExemptionName = document.getElementById("vat_exemption_name");
    const vatExemptionReason = document.getElementById("vat_exemption_reason");
    const details = document.getElementById("Details-VatExemption");

    fillVatExemptionName("vat_exemption_name");
    fillVatExemptionReason("vat_exemption_reason");
    fillVatExemptionCheckbox("vat_exemption_checkbox");

    if (!checkbox || !vatExemptionName || !vatExemptionReason || !details) return;

    // Show/hide VAT form
    const checkboxChanged = () => {
        details.open = checkbox.checked;
        details.querySelectorAll("input[type=text], textarea").forEach(it => {
            it.required = checkbox.checked;
        });
    };
    checkbox.addEventListener("change", checkboxChanged);
    checkboxChanged(); // initialize

    // Handle checkout button reliably (even if replaced dynamically)
    document.body.addEventListener("click", async (event) => {
        const checkoutBtn = event.target.closest("#CartDrawer-Checkout-Fake");
        if (!checkoutBtn) return;

        const checkbox = document.getElementById("vat_exemption_checkbox");
        const vatExemptionName = document.getElementById("vat_exemption_name");
        const vatExemptionReason = document.getElementById("vat_exemption_reason");

        if (checkbox.checked && 
            (vatExemptionName.value.trim() === "" || vatExemptionReason.value.trim() === "")) {
            event.preventDefault();
            alert("Please complete the VAT exemption form or untick the exemption box to continue.");
            return;
        }

        event.preventDefault(); // Prevent default navigation
        checkoutBtn.disabled = true; // Disable button
        checkoutBtn.setAttribute("aria-busy", "true"); // Indicate it's busy
        checkoutBtn.textContent = "Processing...";
        try {
            await swapVat(!checkbox.checked);
            
            // Save session storage values
            sessionStorage.setItem("vatExemptionCheckbox", checkbox.checked);
            sessionStorage.setItem("vatExemptionName", vatExemptionName.value);
            sessionStorage.setItem("vatExemptionReason", vatExemptionReason.value);

            window.location.href = "/cart"; // Go to basket after swap
        } catch (err) {
            alert("Something went wrong updating your cart. Please try again.");
            console.error(err);
        } finally {
            checkoutBtn.disabled = false; // Re-enable button even if swapVat fails
            checkoutBtn.removeAttribute("aria-busy"); // Clear busy state
            checkoutBtn.textContent = "Proceed to Checkout"; // Restore original text
        }
    });
});
